import React from 'react';
import { Info, HelpCircle, ExternalLink, AlertTriangle } from 'lucide-react';

/**
 * Информационный компонент с подробной информацией о конвертере валют,
 * курсах, методах конвертации и полезных советах
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center">
        <Info className="mr-2 h-6 w-6 text-indigo-500" />
        О конвертере валют
      </h2>
      
      <div className="prose dark:prose-invert max-w-none prose-indigo">
        <section>
          <h3>Что такое конвертер валют?</h3>
          <p>
            Конвертер валют — это инструмент для мгновенного перерасчета сумм из одной валюты в другую 
            по актуальному обменному курсу. Наш конвертер использует данные в режиме реального времени 
            для обеспечения максимальной точности конвертации.
          </p>
          <p>
            Конвертер особенно полезен при планировании путешествий, международных покупках, 
            инвестициях в иностранные активы, а также для отслеживания стоимости валютных накоплений.
          </p>
        </section>
        
        <section className="mt-8">
          <h3>Как рассчитываются курсы валют?</h3>
          <p>
            Курсы валют формируются на основе спроса и предложения на валютных рынках. 
            На их значение влияют многие факторы:
          </p>
          <ul>
            <li><strong>Экономические показатели</strong> — такие как ВВП, инфляция, безработица.</li>
            <li><strong>Денежно-кредитная политика</strong> — ключевая ставка центрального банка.</li>
            <li><strong>Геополитические события</strong> — выборы, конфликты, международные соглашения.</li>
            <li><strong>Рыночные настроения</strong> — уровень доверия инвесторов, спекулятивные операции.</li>
            <li><strong>Торговый баланс страны</strong> — соотношение экспорта и импорта.</li>
          </ul>
          <p>
            Наш конвертер использует среднерыночные курсы (Mid-market rates) — среднее значение между 
            ценой покупки и продажи валюты на международном валютном рынке. Это наиболее объективный 
            показатель стоимости валюты.
          </p>
        </section>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-6">
          <h4 className="flex items-center text-blue-800 dark:text-blue-300 font-medium mb-2">
            <HelpCircle className="h-5 w-5 mr-2" />
            Важно знать
          </h4>
          <p className="text-blue-700 dark:text-blue-200 text-sm">
            Курсы, используемые банками и обменными пунктами, обычно менее выгодны, чем среднерыночные, 
            так как они включают комиссии и наценки. Поэтому фактический обменный курс может отличаться от 
            указанного в нашем конвертере.
          </p>
        </div>
        
        <section className="mt-8">
          <h3>Основные мировые валюты</h3>
          <p>
            В мире существует около 180 национальных валют, но лишь некоторые из них имеют 
            статус мировых резервных валют:
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Название</th>
                  <th>Страна/Регион</th>
                  <th>Символ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>USD</td>
                  <td>Доллар США</td>
                  <td>США</td>
                  <td>$</td>
                </tr>
                <tr>
                  <td>EUR</td>
                  <td>Евро</td>
                  <td>Еврозона</td>
                  <td>€</td>
                </tr>
                <tr>
                  <td>JPY</td>
                  <td>Иена</td>
                  <td>Япония</td>
                  <td>¥</td>
                </tr>
                <tr>
                  <td>GBP</td>
                  <td>Фунт стерлингов</td>
                  <td>Великобритания</td>
                  <td>£</td>
                </tr>
                <tr>
                  <td>CNY</td>
                  <td>Юань</td>
                  <td>Китай</td>
                  <td>¥</td>
                </tr>
                <tr>
                  <td>RUB</td>
                  <td>Российский рубль</td>
                  <td>Россия</td>
                  <td>₽</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        
        <section className="mt-8">
          <h3>Как пользоваться конвертером валют</h3>
          <ol>
            <li>Введите сумму, которую хотите конвертировать.</li>
            <li>Выберите исходную валюту из выпадающего списка.</li>
            <li>Выберите целевую валюту, в которую нужно перевести сумму.</li>
            <li>Результат конвертации отобразится автоматически.</li>
            <li>Для обратной конвертации используйте кнопку со стрелками между валютами.</li>
            <li>Чтобы сохранить результат конвертации, нажмите кнопку "Сохранить".</li>
          </ol>
          <p>
            Вы также можете просмотреть график изменения курса выбранной валютной пары за последнее время, 
            что помогает отслеживать тенденции и выбирать оптимальный момент для обмена валюты.
          </p>
        </section>
        
        <section className="mt-8">
          <h3>Советы по обмену валюты</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="text-green-800 dark:text-green-300 font-medium mb-2">Выгодно</h4>
              <ul className="text-sm text-green-700 dark:text-green-200 space-y-2">
                <li>Использовать онлайн-сервисы для сравнения курсов</li>
                <li>Обменивать крупные суммы (лучший курс)</li>
                <li>Планировать обмен заранее, следя за трендами</li>
                <li>Использовать мультивалютные карты</li>
                <li>Обменивать в будний день до обеда</li>
              </ul>
            </div>
            
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
              <h4 className="text-red-800 dark:text-red-300 font-medium mb-2">Невыгодно</h4>
              <ul className="text-sm text-red-700 dark:text-red-200 space-y-2">
                <li>Обменивать в аэропортах и туристических зонах</li>
                <li>Снимать валюту с карты за границей</li>
                <li>Откладывать обмен до последнего момента</li>
                <li>Игнорировать скрытые комиссии</li>
                <li>Использовать уличные обменники без лицензии</li>
              </ul>
            </div>
          </div>
        </section>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
          <h4 className="flex items-center text-yellow-800 dark:text-yellow-300 font-medium mb-2">
            <AlertTriangle className="h-5 w-5 mr-2" />
            Предостережение
          </h4>
          <p className="text-yellow-700 dark:text-yellow-200 text-sm">
            Обменные курсы постоянно колеблются, и никакой конвертер валют не может гарантировать 
            100% точность будущих курсов. Информация, предоставляемая нашим инструментом, 
            предназначена только для ознакомления и не является финансовой рекомендацией.
          </p>
        </div>
        
        <section className="mt-8">
          <h3>Дополнительные возможности нашего конвертера</h3>
          <ul>
            <li><strong>Сохранение конвертаций</strong> — возможность сохранять результаты для дальнейшего использования.</li>
            <li><strong>График изменения курса</strong> — наглядное отображение динамики курса выбранной валютной пары.</li>
            <li><strong>Таблица курсов</strong> — основные обменные курсы относительно выбранной базовой валюты.</li>
            <li><strong>Автоматическое обновление</strong> — регулярное обновление курсов для обеспечения актуальности данных.</li>
            <li><strong>Экспорт результатов</strong> — возможность сохранить результат конвертации в файл для отчетности.</li>
          </ul>
        </section>
        
        <hr className="my-8" />
        
        <section className="text-sm text-gray-600 dark:text-gray-400">
          <p>
            Курсы валют обновляются регулярно с использованием надежных источников финансовой информации.
            Последние обновления происходят в автоматическом режиме с интервалом до 30 минут.
          </p>
          <p className="mt-2">
            Для получения официальных курсов валют, пожалуйста, обращайтесь к ресурсам центральных банков 
            и авторизованных финансовых учреждений.
          </p>
        </section>
      </div>
    </article>
  );
};

export default InfoContent;